import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { F } from "@faire/web--source/slate/icons/F";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import range from "lodash/range";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";
var PRODUCT_IMAGES_URLS = [
    "https://cdn.faire.com/fastly/0ca312a3225f9523b4dae8eed88d8308b4cd7d51b4117ab849c894d767f9f1d8.png",
    "https://cdn.faire.com/fastly/45077ac8889bae01d59daba748f3dc4104cb7657d95e0472ac9dab21f3a33c09.png",
    "https://cdn.faire.com/fastly/9c80610a386ef26b0d68f22d43bf75910efe67ac329b38f1f92e9fde793d4057.png",
    "https://cdn.faire.com/fastly/34a5ea2c667e5a8e74ea94a3c1fd9fb6bc858b8d3a139488b72b3517a45c34fe.png",
    "https://cdn.faire.com/fastly/fb5c936eb0bca57cf1a6b8cb049110371c61d409910ee163b4a7c622ea5842c0.png",
    "https://cdn.faire.com/fastly/442569bed5b03f093d7e29b6de00f8c2bf64e038b6e22c11a92d3d9e61a42a2c.png",
    "https://cdn.faire.com/fastly/76bf587151ce7afa90cb8ee1c7af04f42edd34fb704b1ed1123870c0a9974145.png"
];
var ANIMATION_INTERVAL_MS = 1000;
var TRANSITION_DURATION_MS = 250;
var PRODUCT_COUNT_PROGRESSION = [
    0,
    3,
    7
];
export var SaveTimeIllustration = function() {
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var isSSR = useIsSSR();
    var _React_useState = _sliced_to_array(React.useState(0), 2), loadingStage = _React_useState[0], setLoadingStage = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(0), 2), productsLoadedCount = _React_useState1[0], setProductsLoadedCount = _React_useState1[1];
    useEffect(function() {
        var index = 0;
        var intervalId = setInterval(function() {
            var _PRODUCT_COUNT_PROGRESSION_index;
            setProductsLoadedCount((_PRODUCT_COUNT_PROGRESSION_index = PRODUCT_COUNT_PROGRESSION[index]) !== null && _PRODUCT_COUNT_PROGRESSION_index !== void 0 ? _PRODUCT_COUNT_PROGRESSION_index : 0);
            setLoadingStage(index);
            index = (index + 1) % PRODUCT_COUNT_PROGRESSION.length; // Cycle through 0, 1, 2
        }, ANIMATION_INTERVAL_MS);
        return function() {
            return clearInterval(intervalId);
        };
    }, []);
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(TabletAndBelowContainer, {
            align: "center",
            children: [
                /*#__PURE__*/ _jsx(IconContainer, {
                    children: /*#__PURE__*/ _jsx(F, {
                        fontSize: "20px"
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: "2x"
                }),
                /*#__PURE__*/ _jsx(LoadingDotContainer, {
                    children: range(3).map(function(i) {
                        return /*#__PURE__*/ _jsx(LoadingDot, {
                            $isLoading: loadingStage < i
                        }, i);
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: "2x"
                }),
                /*#__PURE__*/ _jsx(TableContainer, {
                    gap: "2x",
                    children: PRODUCT_IMAGES_URLS.map(function(imageUrl, index) {
                        return /*#__PURE__*/ _jsx(ProductRow, {
                            imageUrl: index < productsLoadedCount ? imageUrl : undefined
                        }, index);
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(DesktopContainer, {
        $imageUrl: optimizeImageUrl({
            url: "https://cdn.faire.com/fastly/07b62ef13f7fccb3041daf3be4706f7b518c8199a69a5878b0917a7478ac1eb8.png"
        }, isSSR),
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                align: "center",
                justify: "center",
                gap: "1x",
                children: [
                    /*#__PURE__*/ _jsx(IconContainer, {
                        children: /*#__PURE__*/ _jsx(F, {
                            fontSize: "30px"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.inverse,
                        variant: "labelSansMedium",
                        children: "Faire"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "3x"
            }),
            /*#__PURE__*/ _jsx(LoadingDotContainer, {
                children: range(3).map(function(i) {
                    return /*#__PURE__*/ _jsx(LoadingDot, {
                        $isLoading: loadingStage < i
                    }, i);
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "3x"
            }),
            /*#__PURE__*/ _jsxs(TableContainer, {
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                width: "9x"
                            }),
                            /*#__PURE__*/ _jsx(TableHeader, {
                                $width: 106,
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    variant: "labelSansMedium",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "ChWRN6",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Name"
                                            }
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                width: 20
                            }),
                            /*#__PURE__*/ _jsx(TableHeader, {
                                $width: 132,
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    variant: "labelSansMedium",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "aDXYio",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Description"
                                            }
                                        ]
                                    })
                                })
                            })
                        ]
                    }),
                    PRODUCT_IMAGES_URLS.map(function(imageUrl, index) {
                        return /*#__PURE__*/ _jsx(ProductRow, {
                            imageUrl: index < productsLoadedCount ? imageUrl : undefined
                        }, index);
                    })
                ]
            })
        ]
    });
};
var ProductRow = function(param) {
    var imageUrl = param.imageUrl;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var isSSR = useIsSSR();
    var url = imageUrl ? optimizeImageUrl({
        url: imageUrl
    }, isSSR) : undefined;
    var isLoading = !imageUrl;
    if (isTabletAndBelow) {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: "1x",
            align: "center",
            children: [
                /*#__PURE__*/ _jsx(ProductImageWrapper, {
                    children: /*#__PURE__*/ _jsx(ProductImage, {
                        $imageUrl: url
                    })
                }),
                /*#__PURE__*/ _jsx(NameField, {
                    $isLoading: isLoading
                }),
                /*#__PURE__*/ _jsx(DescriptionField, {
                    $isLoading: isLoading
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsx(HorizontalLine, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gap: "2x",
                align: "center",
                children: [
                    /*#__PURE__*/ _jsx(ProductImageWrapper, {
                        children: /*#__PURE__*/ _jsx(ProductImage, {
                            $imageUrl: url
                        })
                    }),
                    /*#__PURE__*/ _jsx(NameField, {
                        $isLoading: isLoading
                    }),
                    /*#__PURE__*/ _jsx(DescriptionField, {
                        $isLoading: isLoading
                    })
                ]
            })
        ]
    });
};
var TabletAndBelowContainer = styled(Row).withConfig({
    componentId: "sc-e2ff0329-0"
})([
    "height:343px;width:100%;max-width:584px;background-color:",
    ";padding-left:",
    ";user-select:none;"
], Decorative.shade.green, getSpacing("3x"));
var DesktopContainer = styled(Row).withConfig({
    componentId: "sc-e2ff0329-1"
})([
    "height:584px;width:100%;max-width:584px;background-image:url(",
    ");background-repeat:no-repeat;background-size:cover;padding-left:",
    ";user-select:none;"
], function(props) {
    return props.$imageUrl;
}, getSpacing("6x"));
var IconContainer = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-e2ff0329-2"
})([
    "width:",
    ";height:",
    ";border-radius:10px;background-color:",
    ";flex-shrink:0;",
    "{width:",
    ";height:",
    ";border-radius:4px;}"
], getSpacing("8x"), getSpacing("8x"), Core.surface.inverse, responsiveMediaQueries.tabletAndBelow, getSpacing("5x"), getSpacing("5x"));
var LoadingDotContainer = styled(Row).attrs({
    align: "center",
    gap: 6
}).withConfig({
    componentId: "sc-e2ff0329-3"
})([
    "margin-top:",
    ";flex-shrink:0;",
    "{margin-top:0;}"
], getSpacing("-3x"), responsiveMediaQueries.tabletAndBelow);
var LoadingDot = styled.div.withConfig({
    componentId: "sc-e2ff0329-4"
})([
    "width:6px;height:6px;border-radius:50%;background-color:",
    ";opacity:",
    ";"
], Core.icon.inverse, function(props) {
    return props.$isLoading ? "50%" : "100%";
});
var TableContainer = styled(Column).withConfig({
    componentId: "sc-e2ff0329-5"
})([
    "width:100%;height:544px;margin-top:",
    ";padding:",
    " 0 0 ",
    ";background-color:",
    ";overflow:hidden;",
    "{width:100%;height:319px;margin-top:",
    ";padding:",
    " 0 0 ",
    ";}"
], getSpacing("5x"), getSpacing("5x"), getSpacing("5x"), Core.surface.inverse, responsiveMediaQueries.tabletAndBelow, getSpacing("3x"), getSpacing("2x"), getSpacing("2x"));
var TableHeader = styled.div.withConfig({
    componentId: "sc-e2ff0329-6"
})([
    "width:",
    "px;"
], function(props) {
    return props.$width;
});
var NameField = styled.div.withConfig({
    componentId: "sc-e2ff0329-7"
})([
    "width:106px;height:12px;background-color:",
    ";border-radius:1px;transition:background-color ",
    "ms ease-in-out;",
    "{width:61px;flex-grow:1;}"
], function(props) {
    return props.$isLoading ? Core.surface.subdued : Core.border.muted;
}, TRANSITION_DURATION_MS, responsiveMediaQueries.tabletAndBelow);
var DescriptionField = styled.div.withConfig({
    componentId: "sc-e2ff0329-8"
})([
    "width:132px;height:12px;background-color:",
    ";border-radius:1px;transition:background-color ",
    "ms ease-in-out;",
    "{width:84px;flex-grow:1;}"
], function(props) {
    return props.$isLoading ? Core.surface.subdued : Core.border.muted;
}, TRANSITION_DURATION_MS, responsiveMediaQueries.tabletAndBelow);
var ProductImageWrapper = styled.div.withConfig({
    componentId: "sc-e2ff0329-9"
})([
    "width:",
    ";height:",
    ";border-radius:2px;background-color:",
    ";flex-shrink:0;",
    "{width:",
    ";height:",
    ";}"
], getSpacing("7x"), getSpacing("7x"), Core.surface.subdued, responsiveMediaQueries.tabletAndBelow, getSpacing("5x"), getSpacing("5x"));
var ProductImage = styled.div.withConfig({
    componentId: "sc-e2ff0329-10"
})([
    "width:100%;height:100%;border-radius:2px;background-image:",
    ";background-repeat:no-repeat;background-size:cover;background-color:",
    ";flex-shrink:0;opacity:",
    ";transition:opacity ",
    "ms ease-in-out;"
], function(props) {
    return props.$imageUrl ? css([
        "url(",
        ")"
    ], props.$imageUrl) : "none";
}, Core.surface.subdued, function(props) {
    return props.$imageUrl ? 1 : 0;
}, TRANSITION_DURATION_MS);
